<script>
    // This is a ContactCard.svelte with different copy :X
    // intended to capture an email on initial log-in if
    // we are dealing with an IAP for an email-less account.

    import Button from "../Button.svelte";
    import CustomCheckbox from "../CustomCheckbox.svelte";
    import FlatCard from "../FlatCard.svelte";
    import HorizontalRule from "../presentational/HorizontalRule.svelte";
    import { processNewCredentials } from "../../utilities/identity.js";
    import { createEventDispatcher, onMount } from "svelte";
    import { updateEmail, updateEmailConfirm } from "../../api/client.js";

    const dispatch = createEventDispatcher();

    // data the card displays
    // export let email = "",

    let email = "",
        code,
        activeStepIdx = 0,
        emailResponse = {},
        steps = [],
        emailInputField,
        editedEmailAddress;

    let boolPersistLogin = false;
    $: doPersistLogin = boolPersistLogin;

    /*
    TODO abstract to a "SteppedCard" component maybe.

        Cards (data-step=)
        0 Please enter your e-mail address so we can send you a code.
        1 Please enter the code we sent to your e-mail address (in #0).
        2 Please re-enter your code that we sent (in #0).
        3 Email not assoc. with account
    */
    const navigateToIndex = (newIdx) => {
        // move the display to the card with index newIdx (if newIdx is valid.)
        if (activeStepIdx === newIdx || !Number.isInteger(+newIdx) || newIdx < 0 || newIdx >= steps.length) return;
        // navigate and update active idx
        steps[activeStepIdx].classList.add("hidden");
        steps[newIdx].classList.remove("hidden");
        activeStepIdx = newIdx;

        if (activeStepIdx === 1 && emailInputField) {
            emailInputField.focus();
        }
    };

    const handleEmailSubmit = () => {
        updateEmail({
            dataToUpdate: { email },
            onSuccess: function ({ data }) {
                emailResponse = data;
                console.log(data);
                navigateToIndex(1);
            },
            onError: function (err) {
                console.log(err);
                navigateToIndex(4);
            },
        });
    };

    // handleCodeSubmit sends the code the user enters that was e-mailed to their inbox
    // to the server, along with the initial e-mail response. The initial e-mail response
    // contains an auth_id -- xsrf token such that you cannot simply guess at codes.
    const handleCodeSubmit = () => {
        updateEmailConfirm({
            dataToUpdate: { email, auth_code: code, auth_id: emailResponse.auth_id },
            statusHandlers: {
                "200": ({ data }) => {
                    processNewCredentials(data);
                    navigateToIndex(0);
                    dispatch("message", {
                        // TODO have more granular message than this that updates ALL the state.
                        action: "STATE_UPDATED",
                        data: {},
                    });
                },
                "409": function () {
                    // email verified but already in use
                    navigateToIndex(4);
                },
            },
            onError: function (err) {
                code = null;
                navigateToIndex(2);
                console.warn("handleCodeSubmit error:", err);
            },
        });
    };

    onMount(() => {
        editedEmailAddress = email.address;

        // This bit of code attaches script behavior to translated page tags post-translation like
        // "click here to try again" navigating the user back to card with index 0.
        const fl = document.getElementsByClassName("navigateToIndex0") || [];
        Array.from(fl).forEach(link => {
            link.addEventListener("click", e => {
                e.preventDefault();
                navigateToIndex(0);
            });
        });
    });

    import { t } from "../../i18n";
</script>

<!--src/components/composites/LoginCard.svelte-->
<FlatCard boxShadow="true">
    <div class="card-content" data-step="0" bind:this="{steps[0]}">
        <h2>{$t('add_an_email')}</h2>
        <HorizontalRule />
        <div class="flex-row collapse-to-col">
            {$t('add_email_paragraph')}
        </div>
        <form on:submit|preventDefault="{handleEmailSubmit}">
            <div class="row flex-row collapse-to-col">
                <input
                    bind:value="{email}"
                    placeholder="{$t('email_address')}"
                    type="email"
                    name="email"
                    minlength="3"
                    required
                    aria-label="{$t('email_address')}"
                    class="solid-silver-border rounded-edge"
                />
                <Button buttonText="{$t('submit')}" />
            </div>
        </form>
        <HorizontalRule />
        <div class="footer flex-row">
            {$t("having_trouble")}
            {@html $t('visit_our_help_center', {"$opentag$": "<a href=\"https://vpnsupport.getadblock.com/\" target=\"_blank\" rel=\"noopener noreferrer\">", "$closetag$": "</a>"})}
        </div>
    </div>
    <div class="card-content hidden" data-step="1" bind:this="{steps[1]}">
        <h2>{$t("enter_your_code")}</h2>
        <HorizontalRule />
        <div class="flex-row">{$t('we_sent_a_code')} {$t('enter_the_code_here')}</div>
        <form on:submit|preventDefault="{handleCodeSubmit}">
            <div class="row flex-row collapse-to-col">
                <input
                    bind:value="{code}"
                    placeholder="------"
                    type="text"
                    pattern="^[0-9]+$"
                    autocomplete="off"
                    name="email-code"
                    required
                    aria-label="Verification Code Sent to Your E-mail"
                    class="solid-silver-border rounded-edge code"
                />
                <Button buttonText="{$t('submit')}" />
            </div>
            <div class="row flex-row no-wrap collapse-v-margin">
                <CustomCheckbox
                    isSelected="{doPersistLogin}"
                    on:click="{() => {
                        boolPersistLogin = !boolPersistLogin;
                    }}"
                />
                <span on:click="{() => {
                    boolPersistLogin = !boolPersistLogin;
                }}" class="clickable"
                >{$t('keep_me_logged_in', {"$replace$": 30})}</span>
            </div>
        </form>
        <HorizontalRule />
        <div class="footer flex-row">
            <b>{$t('didnt_get_the_code')}</b>
            {@html $t('enter_your_email_again', {"$opentag$": "<a href=\"#\" class=\"navigateToIndex0\">", "$closetag$": "</a>"})}
        </div>
    </div>
    <div class="card-content hidden" data-step="2" bind:this="{steps[2]}">
        <h2>{$t('oops')}</h2>
        <HorizontalRule />
        <div class="flex-row">{$t('dont_recognize_code')} {$t('please_try_again')}</div>
        <form on:submit|preventDefault="{handleCodeSubmit}">
            <div class="row flex-row collapse-to-col">
                <input
                    bind:value="{code}"
                    placeholder="------"
                    type="text"
                    pattern="^[0-9]+$"
                    autocomplete="off"
                    name="email-code"
                    required
                    aria-label="Verification Code Sent to Your E-mail"
                    class="solid-silver-border rounded-edge code"
                />
                <Button buttonText="{$t('submit')}" />
            </div>
            <div class="row flex-row no-wrap collapse-v-margin">
                <CustomCheckbox
                    isSelected="{doPersistLogin}"
                    on:click="{() => {
                        boolPersistLogin = !boolPersistLogin;
                    }}"
                />
                <span on:click="{() => {
                    boolPersistLogin = !boolPersistLogin;
                }}" class="clickable"
                >{$t('keep_me_logged_in', {"$replace$": 30})}</span>
            </div>
        </form>
        <HorizontalRule />
        <div class="footer flex-row">
            <b>{$t('still_not_working')}</b>
            {@html $t('try_different_email',
                {
                    "$opentag$": "<a href=\"#\" class=\"navigateToIndex0\">",
                    "$closetag$": "</a>",
                    "$opentag2$": "<a href=\"https://vpnsupport.getadblock.com/\" target=\"_blank\" rel=\"noopener noreferrer\">",
                    "$closetag2$": "</a>",
                })
            }
        </div>
    </div>
    <div class="card-content hidden" data-step="3" bind:this="{steps[3]}">
        <h2>{$t('oops')}</h2>
        <HorizontalRule />
        <div class="row">
            <p>
                {$t('email_doesnt_have_account')}
                {$t('subscribe_to_vpn_to_create_account', {
                    "$opentag$": "<a href=\"https://vpn.getadblock.com/purchase/\">",
                    "$closetag$": "</a>",
                })}
            </p>
        </div>
        <HorizontalRule />
        <div class="footer flex-row">
            <strong>{$t('still_having_issues')}</strong>
            {@html $t('try_different_email',
            {
                "$opentag$": "<a href=\"#\" class=\"navigateToIndex0\">",
                "$closetag$": "</a>",
                "$opentag2$": "<a href=\"https://vpnsupport.getadblock.com/\" target=\"_blank\" rel=\"noopener noreferrer\">",
                "$closetag2$": "</a>",
            })
            }
        </div>
    </div>
    <div class="card-content hidden" data-step="4" bind:this="{steps[4]}">
        <h2>{$t('somethings_wrong')}</h2>
        <HorizontalRule />
        <div class="row">
            <p>
                {$t('we_cant_connect')}
                {@html $t('try_different_email',
                {
                    "$opentag$": "<a href=\"#\" class=\"navigateToIndex0\">",
                    "$closetag$": "</a>",
                    "$opentag2$": "<a href=\"https://vpnsupport.getadblock.com/\" target=\"_blank\" rel=\"noopener noreferrer\">",
                    "$closetag2$": "</a>",
                })
                }
            </p>
        </div>
    </div>
</FlatCard>

<style>
    .collapse-v-margin {
        margin-top: -24px;
    }
    .card-content .flex-row {
        margin: 24px auto;
    }
    input {
        min-height: 40px;
        padding-left: 8px;
        flex: 2 1 33%;
        margin-right: 24px;
    }

    .clickable {
        cursor: pointer;
        user-select: none;
    }
</style>
